/**
 * Per-route lazy-import warmers. Calling one of these triggers the chunk
 * download but does NOT mount the component — so when the user actually
 * navigates, React.lazy() resolves instantly from cache and the page
 * paints without a Suspense flash.
 *
 * Map keys mirror the router paths so the sidebar can do:
 *
 *   <NavLink onMouseEnter={() => prefetchRoute('/admin/tournaments')} />
 *
 * Adding a route here is optional — only routes that admins / players
 * frequently navigate to benefit from prefetching. Marketing/legal pages
 * stay lazy without prefetch hints (rarely visited from the main flow).
 *
 * Each entry uses an idempotent factory: import() returns the same
 * promise across calls, so we never double-fetch the chunk on rapid hover.
 */
const PREFETCHERS = {
  // Admin
  '/admin/dash':         () => import('@/pages/DashboardPage'),
  '/admin/tournaments':  () => import('@/pages/TournamentsPage'),
  '/admin/branding':     () => import('@/pages/BrandingPage'),
  '/admin/billing':      () => import('@/pages/BillingPage'),
  '/admin/settings':     () => import('@/pages/SettingsPage'),
  '/admin/season':       () => import('@/pages/SeasonLeaderboardPage'),
  '/admin/profile':      () => import('@/pages/ProfilePage'),
  '/admin/live':         () => import('@/pages/AdminLiveScoresPage'),

  // CMS
  '/cms/dashboard':      () => import('@/pages/cms/CmsDashboardPage'),
  '/cms/tournaments':    () => import('@/pages/cms/CmsTournamentsPage'),
  '/cms/clients':        () => import('@/pages/cms/CmsClientsPage'),
  '/cms/players':        () => import('@/pages/cms/CmsPlayersPage'),
  '/cms/tiers':          () => import('@/pages/cms/CmsTiersPage'),
  '/cms/settings':       () => import('@/pages/cms/CmsSettingsPage'),
  '/cms/emails':         () => import('@/pages/cms/CmsEmailTemplatesPage'),
  '/cms/api-monitor':    () => import('@/pages/cms/CmsApiMonitorPage'),
  '/cms/reports':        () => import('@/pages/cms/CmsReportsPage'),
  '/cms/support':        () => import('@/pages/cms/CmsSupportPage'),
  '/cms/beta-access':    () => import('@/pages/cms/CmsBetaAccessPage'),
  '/cms/invite-codes':   () => import('@/pages/cms/CmsInviteCodesPage'),

  // Player — small surface (Dash + Live + Profile) but the chunks are
  // ~50 KB each, so prefetching on hover/touch is still a noticeable win.
  '/player/dash':        () => import('@/pages/player/PlayerDashboardPage'),
  // '/player/live' removed — redirects to /player/dash via App router.
  '/player/profile':     () => import('@/pages/player/PlayerProfilePage'),
};

const warmed = new Set();

export function prefetchRoute(path) {
  if (warmed.has(path)) return;
  const fn = PREFETCHERS[path];
  if (!fn) return;
  warmed.add(path);
  // Fire-and-forget — silence errors so a transient chunk-fetch hiccup
  // doesn't trigger a console error on a hover (the real navigation will
  // retry the import and surface the error there if it's persistent).
  fn().catch(() => warmed.delete(path));
}
